import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { Project } from '@data/models/project.model';
import { User } from '@data/models/user.model';
import { buildPageSizeOptions } from '@shared/utils/utilities';
import { FilterService } from '@shared/services/filter.service';
import { MatInput } from '@angular/material/input';
import { Subject } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { DrawerService } from '../drawer';
import { HeaderActionsService } from '../layout-detail/header-actions-service';
import { sortingDataAccessor } from '@shared/utils/angular-material-tools';

const DEFAULT_LIST_SIZE = 10;
const MAX_BULK_EDIT_PROJECT_LIMIT = 500;

@Component({
    selector: 'syn-projects-table',
    templateUrl: 'projects-table.component.html',
    styleUrls: ['projects-table.component.scss']
})
export class ProjectsTableComponent implements OnInit, AfterViewInit, OnChanges {

    @Input() projects: Project[];
    @Input() user: User;
    @Input() displayedColumns = ['id', 'name', 'status', 'business-code', 'groups', 'tags'];
    @Input() message: string;
    @Input() noDataMessage = 'Aucun projet.';
    @Input() pageSize: Number;
    @Input() pageSizeOptions: Number[] = [];

    @Input() enableBulkEdit = false;

    @Output() refresh = new EventEmitter<void>();

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('drawerContent', {static: true}) drawerContent: TemplateRef<{}>;

    edit = false;
    maxBulkEditProjectLimit = MAX_BULK_EDIT_PROJECT_LIMIT;

    dataSource: MatTableDataSource<Project>;
    selection = new SelectionModel<Project>(true, []);
    showPaginator = false;
    query: string;
    displayUnprovisionnedAndClosedProjects = false;

    private changePageSizeOptions$ = new Subject();

    constructor(
        private headerActionsService: HeaderActionsService,
        private filterService: FilterService,
        private readonly drawerService: DrawerService<any>,
    ) {}

    public get columnDisplayed() {
        if (this.edit) {
            return ['select', ...this.displayedColumns];
        }

        return this.displayedColumns;
    }

    public get hasReachedBulkProjectUpdateLimit() {
        return this.selection.selected.length > MAX_BULK_EDIT_PROJECT_LIMIT;
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    ngOnInit() {
        this.changePageSizeOptions$.subscribe({
            next: (value: unknown[]) => {
                this.pageSizeOptions = buildPageSizeOptions(value);
            }
        });
        if (this.user) {
            this.displayedColumns = ['id', 'name', 'user-role', 'status', 'business-code', 'groups', 'tags'];
        }
        this.dataSource = new MatTableDataSource<Project>(
            this.filterService.filterProjectsByStatus(this.projects || [])
        );
        this.dataSource.filterPredicate = (data, filter: string) => {
            const propertiesToFilter = [
                'id', 'name', 'status', 'businessCode', 'groups', 'tags', 'deliveryManager',
                'backupDeliveryManager', 'buildProjectManager', 'appManagers'
            ];
            const searchString = filter.trim().toLowerCase();
            return propertiesToFilter.some(property =>
                data[property] && data[property].toString().toLowerCase().includes(searchString)
            );

        };

        this.changePageSizeOptions$.next(this.dataSource.data);
        this.pageSize = this.pageSizeOptions.length === 0 ? 0 : DEFAULT_LIST_SIZE;
        this.showPaginator = (this.projects.length > DEFAULT_LIST_SIZE);

        if (this.projects?.length && this.enableBulkEdit) {
            this.updateHeaderButton();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.projects) {
            this.loadProjects(changes.projects.currentValue);
        }
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.filteredData.length;
        return numSelected === numRows;
    }

    masterToggle(): void | boolean {
        let dataSourceStatus = this.isAllSelected() ? this.selection.clear() : this.dataSource.filteredData.forEach(row => this.selection.select(row));
        dataSourceStatus = undefined;
        return dataSourceStatus;
    }

    async onToggleChange(isChecked: boolean) {
        this.displayUnprovisionnedAndClosedProjects = isChecked;
        this.dataSource.data = isChecked ? this.projects : this.filterService.filterProjectsByStatus(this.projects);
        this.changePageSizeOptions$.next(this.dataSource.data);
    }

    toggleCheckbox(event: unknown, row: Project & { isEdit: boolean }): void | boolean {
        return event ? this.selection.toggle(row) : null;
    }

    openDrawer() {
        this.drawerService.open(this.drawerContent, {
            hasBackdrop: true
        });
    }

    closeDrawer() {
        this.drawerService.close();
    }

    applyFilter(searchFilter: string, input?: MatInput) {
        this.query = searchFilter.trim().toLowerCase();
        this.dataSource.filter = this.query;
        this.changePageSizeOptions$.next(this.dataSource.filteredData);

        if (input) {
            input.value = searchFilter;
        }
    }

    refreshProjects() {
        this.refresh.next();
    }

    public toggleModification() {
        this.edit = !this.edit;
        this.updateHeaderButton();

        if (!this.edit) {
            this.selection.clear();
        }
    }

    private updateHeaderButton(): void {
        this.headerActionsService.clearHeaderActionsAdditional();
        this.headerActionsService.addHeaderButtonAdditional({
            name: this.edit ? 'Annuler' : 'Modifier des projets',
            icon: 'pencil-outline',
            click: () => this.toggleModification()
        });
    }

    private loadProjects(projects: Project[]) {
        // When refreshing the projects, we need to map the selected IDs.
        if (!this.selection.isEmpty()) {
            this.rebindSelection(projects);
        }

        const editableProject = this.filterService.filterProjectsByStatus(projects);
        this.dataSource = new MatTableDataSource<Project>(editableProject);
        this.dataSource.filter = this.query;
        this.changePageSizeOptions$.next(editableProject);

        this.dataSource.sortingDataAccessor = (item, property) => {
            return sortingDataAccessor(item, property);
        };
        this.pageSize = this.pageSizeOptions.length === 0 ? 0 : DEFAULT_LIST_SIZE;
        this.showPaginator = (editableProject.length > DEFAULT_LIST_SIZE);
    }

    private rebindSelection(projects: Project[]) {
        const previousSelection = this.selection;
        this.selection = new SelectionModel<Project>(true, []);

        for (const previouslySelectedProject of previousSelection.selected) {
            const matchingProject = projects.find(project => project.id === previouslySelectedProject.id);

            if (matchingProject) {
                this.selection.select(matchingProject);
            }
        }
    }
}
